import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, Tooltip } from "react-leaflet";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  useTheme,
  IconButton,
  alpha,
} from "@mui/material";
import LayersIcon from "@mui/icons-material/Layers";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchMunicipalitiesGeoJson } from "../../store/slices/municipalitySlice";
import { getMunicipalityName } from "../../utils/municipalityData";
import { formatInBulgarian } from "../../utils/formatters";
import MapDashboard, { metricLayers } from "./MapDashboard";
import MunicipalitySearch from "./MunicipalitySearch";
import { colorScales } from "../../utils/colorScales";
import "leaflet/dist/leaflet.css";
import "./BulgarianMap.css";

const getNiceBreaks = (min, max, count) => {
  if (max <= min) {
    return Array(count + 1).fill(min);
  }
  const range = max - min;
  const step = range / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(step)));
  const stepMultiple = step / magnitude;

  let niceStep;
  if (stepMultiple < 1.5) {
    niceStep = 1 * magnitude;
  } else if (stepMultiple < 3) {
    niceStep = 2 * magnitude;
  } else if (stepMultiple < 7) {
    niceStep = 5 * magnitude;
  } else {
    niceStep = 10 * magnitude;
  }

  const niceMin = Math.floor(min / niceStep) * niceStep;

  const breaks = [];
  for (let i = 0; i <= count; i++) {
    breaks.push(niceMin + i * niceStep);
  }
  return breaks;
};

const MapLegend = ({ selectedLayer, min, max, colors }) => {
  const layerLabel =
    metricLayers.find((l) => l.id === selectedLayer)?.label || "";

  if (!selectedLayer || min === Infinity || max === -Infinity) {
    return null;
  }

  const breaks = getNiceBreaks(min, max, colors.length);

  return (
    <Box
      sx={{
        p: 1,
        position: "absolute",
        bottom: 10,
        right: { xs: "auto", sm: 10 },
        left: { xs: "50%", sm: "auto" },
        transform: { xs: "translateX(-50%)", sm: "none" },
        width: { xs: "calc(100% - 40px)", sm: "auto" },
        maxWidth: { xs: 320, sm: "none" },
        zIndex: 1000,
        bgcolor: "rgba(255, 255, 255, 0.9)",
        borderRadius: 2,
        boxSizing: "border-box",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          whiteSpace: "nowrap",
          fontWeight: "bold",
          textAlign: { xs: "center", sm: "left" },
          display: "block",
        }}
      >
        {layerLabel}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          justifyContent: { xs: "center", sm: "flex-start" },
          mt: 1,
        }}
      >
        {colors.map((color, index) => {
          const rangeStart = breaks[index];
          const rangeEnd = breaks[index + 1];

          if (rangeEnd === undefined) {
            return null; // Don't render label for the last break
          }

          const label = `${formatInBulgarian(rangeStart)} - ${formatInBulgarian(
            rangeEnd,
          )}`;
          return (
            <Box key={index} display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: color,
                  borderRadius: "2px",
                  border: "1px solid #ddd",
                }}
              />
              <Typography variant="caption" fontSize="0.75rem">
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const BulgarianMap = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hoveredLayerRef = React.useRef(null);
  const geoJsonRef = React.useRef(null);
  const metricsFetched = React.useRef(false);

  const { municipalitiesGeoJson, geoJsonLoading, error } = useSelector(
    (state) => state.municipalities,
  );

  const [selectedLayer, setSelectedLayer] = useState(null);
  const [allMetrics, setAllMetrics] = useState({});
  const [isDashboardOpen, setDashboardOpen] = useState(true);
  const [searchedMunicipality, setSearchedMunicipality] = useState(null);

  const colors = useMemo(
    () => colorScales[selectedLayer] || colorScales.default,
    [selectedLayer],
  );

  const handleInputChange = () => {
    if (selectedLayer) {
      setSelectedLayer(null);
    }
  };

  useEffect(() => {
    dispatch(fetchMunicipalitiesGeoJson());
  }, [dispatch]);

  useEffect(() => {
    const fetchAllMetrics = async () => {
      try {
        const requests = municipalitiesGeoJson.map((m) =>
          axios.get(
            `https://danni.narodnichitalishta.bg/api/municipalities/${m.municipalityCode}/metrics`,
          ),
        );
        const responses = await Promise.all(requests);
        const metricsData = responses.reduce((acc, response, index) => {
          const municipalityCode =
            municipalitiesGeoJson[index].municipalityCode;
          acc[municipalityCode] = response.data;
          return acc;
        }, {});
        setAllMetrics(metricsData);
      } catch (error) {
        console.error("Error fetching all metrics:", error);
      }
    };

    if (municipalitiesGeoJson.length > 0 && !metricsFetched.current) {
      metricsFetched.current = true;
      fetchAllMetrics();
    }
  }, [municipalitiesGeoJson]);

  const metricValues = useMemo(
    () =>
      Object.values(allMetrics)
        .map((m) => (m ? m[selectedLayer] : null))
        .filter((v) => v !== null && v !== undefined),
    [allMetrics, selectedLayer],
  );

  // Sort values to calculate percentiles for clamping outliers
  const sortedMetricValues = useMemo(
    () => [...metricValues].sort((a, b) => a - b),
    [metricValues],
  );

  const minMetricValue = useMemo(() => {
    if (sortedMetricValues.length === 0) return Infinity;
    // Use 5th percentile for min to reduce influence of extreme low values
    const index = Math.floor(sortedMetricValues.length * 0.05);
    return sortedMetricValues[index];
  }, [sortedMetricValues]);

  const maxMetricValue = useMemo(() => {
    if (sortedMetricValues.length === 0) return -Infinity;
    // Use 95th percentile for max to reduce influence of extreme high values
    const index = Math.ceil(sortedMetricValues.length * 0.95) - 1;
    // Ensure index is not negative
    return sortedMetricValues[Math.max(0, index)];
  }, [sortedMetricValues]);

  const getColor = (value, min, max, isSearched = false) => {
    // 1. Първоначално състояние (без избран слой)
    if (!selectedLayer) {
      const baseColor = theme.palette.warning.light;
      // При сърч премахваме прозрачността напълно (1) спрямо дефолтното (0.6)
      return isSearched ? alpha(baseColor, 1) : alpha(baseColor, 0.4);
    }

    // 2. Когато има избран слой, но няма данни за общината
    if (value === null || value === undefined) {
      // Вместо прозрачно, при сърч я правим плътно сива или в най-светлия цвят, но без прозрачност
      return isSearched ? "#666666" : alpha(colors[0], 0.3);
    }

    // 3. Изчисляване на цвета по скалата
    const percentage = max - min === 0 ? 0.5 : (value - min) / (max - min);

    // Clamp the index to be within the bounds of the colors array.
    // This is important because percentile clamping can result in values outside the min/max range.
    let colorIndex = Math.floor(percentage * colors.length);
    colorIndex = Math.max(0, Math.min(colorIndex, colors.length - 1));

    // АКЦЕНТ ПРИ СЪРЧ:
    if (isSearched) {
      // Вземаме най-тъмния възможен цвят от текущата палитра,
      // независимо каква е реалната стойност на общината
      return colors[colors.length - 1];
    }

    return colors[colorIndex];
  };

  const handleMunicipalityClick = (feature) => {
    const props = feature.properties;
    const municipalityCode = props.nuts4;

    if (municipalityCode) {
      navigate(`/municipalities/${municipalityCode}`);
    } else {
      console.error("No nuts4 code found:", props);
    }
  };

  const onEachMunicipality = (feature, layer) => {
    const municipalityCode = feature.properties.nuts4;
    const municipalityName = getMunicipalityName(municipalityCode);

    let tooltipContent = `<div style="min-width: 200px"><strong>${municipalityName}</strong>`;

    if (selectedLayer) {
      const metrics = allMetrics[municipalityCode];
      const value = metrics ? metrics[selectedLayer] : undefined;
      const layerLabel =
        metricLayers.find((l) => l.id === selectedLayer)?.label || "";
      const displayValue =
        typeof value === "number"
          ? Math.round(value).toLocaleString()
          : "Н/А";
      tooltipContent += `<br/>${layerLabel}: ${displayValue}`;
    }

    tooltipContent += `</div>`;

    layer.bindTooltip(tooltipContent, {
      sticky: true,
    });

    layer.on({
      mouseover: (e) => {
        const currentLayer = e.target;

        if (
          hoveredLayerRef.current &&
          hoveredLayerRef.current !== currentLayer
        ) {
          // Връщаме дефолтния стил (ще се извика style функцията отново)
          geoJsonRef.current.resetStyle(hoveredLayerRef.current);
        }

        // Акцентираме само границата и леко усилваме плътността
        currentLayer.setStyle({
          fillOpacity: selectedLayer ? 1 : 0.95, // Леко по-плътно при ховър
          weight: 2,
          color: theme.palette.primary.dark,
        });

        currentLayer.openTooltip();
        hoveredLayerRef.current = currentLayer;
      },
      mouseout: (e) => {
        const layer = e.target;
        // ВРЪЩАМЕ ОРИГИНАЛНИЯ СТИЛ вместо твърди стойности
        geoJsonRef.current.resetStyle(layer);
        layer.closeTooltip();
      },
      click: () => {
        handleMunicipalityClick(feature);
      },
    });
  };

  const style = (feature) => {
    const municipalityCode = feature.properties.nuts4;
    const metrics = allMetrics[municipalityCode];
    const value = metrics ? metrics[selectedLayer] : null;
    const isSearched =
      searchedMunicipality && searchedMunicipality.code === municipalityCode;

    return {
      fillColor: getColor(value, minMetricValue, maxMetricValue, isSearched),
      weight: 1,
      opacity: 1,
      color: "#BDBDBD",
      dashArray: isSearched ? "" : "3",
      // Ако има търсене, намаляваме леко opacity на ВСИЧКИ други, а търсената е на 1
      fillOpacity: isSearched
        ? 1
        : searchedMunicipality
        ? 0.3
        : !selectedLayer
        ? 0.6
        : value === null
        ? 0.2
        : 0.85,
    };
  };
  const geoJsonData = {
    type: "FeatureCollection",
    features: municipalitiesGeoJson.map((m) => ({
      type: "Feature",
      properties: m.properties,
      geometry: m.geometry,
    })),
  };

  if (geoJsonLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="500px"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Зареждане на картата...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="500px"
      >
        <Typography color="error">
          Грешка при зареждане на картата: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        height: "600px",
        minHeight: 500,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1001,
          display: "flex",
          gap: 1,
        }}
      >
        <MunicipalitySearch
          onMunicipalitySelect={setSearchedMunicipality}
          onInputChange={handleInputChange}
        />
        <IconButton
          onClick={() => setDashboardOpen(!isDashboardOpen)}
          sx={{
            backgroundColor: "white",
            "&:hover": {
              backgroundColor: "white",
            },
          }}
        >
          <LayersIcon />
        </IconButton>
      </Box>

      {isDashboardOpen && (
        <MapDashboard
          selectedLayer={selectedLayer}
          onLayerChange={setSelectedLayer}
          onClose={() => setDashboardOpen(false)}
        />
      )}
      <MapContainer
        center={[42.7339, 25.4858]}
        zoom={7}
        minZoom={6}
        maxZoom={8}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {geoJsonData.features.length > 0 && (
          <GeoJSON
            ref={geoJsonRef}
            key={selectedLayer + searchedMunicipality?.code}
            data={geoJsonData}
            style={style}
            onEachFeature={onEachMunicipality}
          />
        )}
      </MapContainer>
      {geoJsonData.features.length > 0 && (
        <MapLegend
          selectedLayer={selectedLayer}
          min={minMetricValue}
          max={maxMetricValue}
          colors={colors}
        />
      )}
    </Paper>
  );
};

export default BulgarianMap;
